<template>
	<f7-page>
		<navbar
			:text="`${currentPlantation.company} > ${
				currentPlantation.explotation
			} > ${currentPlantation.parcel} > ${currentPlantation.sector} > ${
				currentPlantation.name
			} > ${$t('Utilities.nutrition')}`" />
		<!-- En el caso de que estemos editando un plan nutricional, esperamos a que se cargen los datos completamente. -->
		<DxLoadPanel :visible="!loaded" shading-color="rgba(0,0,0,0.4)" />
		<div v-if="loaded">
			<PopupConfirmWizardExit />
			<stepper />
			<reference-value />
			<nutritional-needs />
			<stepper-navigation
				:disabled="disabledNextButton"
				:alert-message="$t('nutritionDetail.descriptionValidation')" />
		</div>
	</f7-page>
</template>
<script>
	import { DxLoadPanel } from 'devextreme-vue/load-panel';
	import { mapActions, mapGetters, mapState } from 'vuex';
	import navbar from '../../../components/NavBar.vue';
	import Stepper from '../../../components/wizardNutrition/stepper/index.vue';
	import StepperNavigation from '../../../components/wizardNutrition/stepper-navigation/index.vue';
	import ReferenceValue from '../../../components/wizardNutrition/referenceValue/index.vue';
	import NutritionalNeeds from '../../../components/wizardNutrition/nutritionalNeeds/index.vue';
	import { fertilizationService } from '../../../services/fertilizationService';
	import PopupConfirmWizardExit from '../../../components/wizardNutrition/popupConfirmWizardExit/index.vue';
	import moment from 'moment';

	export default {
		name: 'NutritionStepOne',
		components: {
			Stepper,
			StepperNavigation,
			navbar,
			ReferenceValue,
			NutritionalNeeds,
			DxLoadPanel,
			PopupConfirmWizardExit,
		},
		props: ['id'],
		data() {
			return {
				fertilization: {},
				loaded: false,
			};
		},
		computed: {
			...mapGetters('nutritionStepper', ['nutritionSteps', 'stepData']),
			...mapState('Plantation', ['currentPlantation']),
			...mapState('Nutrition', ['displayConfirmExitPopup']),
			...mapState('nutritionStepper', [
				'isEditing',
				'editingStepData',
				'isNew',
			]),
			disabledNextButton() {
				if (this.stepData?.planDescription === undefined) return true;
				if (this.stepData?.ph === undefined || this.stepData?.ph === null)
					return true;
				if (this.stepData?.ph < 0 || this.stepData?.ph > 14) return true;
				if (this.stepData.initDate === '') return true;
				return this.stepData?.planDescription.trim().length === 0;
			},
		},
		async mounted() {
			this.loaded = false;
			if (this.id) {
				// obtener datos de plan legacy
				const plan = await fertilizationService.getById(
					this.id,
					this.currentPlantation.companyId,
					false
				);

				// pedir plan schema v2 si no viene en cache o no es para este plan (posible si se ha editado otro plan antes)
				let irrigationV2 = this.stepData?.nutritionIrrigationData;
				const cachedIsForThisPlan =
				irrigationV2 && irrigationV2._planId === this.id && Array.isArray(irrigationV2.data);

				if (!cachedIsForThisPlan) {
					irrigationV2 = await fertilizationService.getById(
						this.id,
						this.currentPlantation.companyId,
						true
					);
				}

				// si v2 viene vacío (planes antiguos), reconstruirlo desde el plan normal
				if (!irrigationV2?.data?.length) {
					irrigationV2 = this.buildIrrigationV2FromLegacyPlan(plan);
				}

				// guardar en store el v2 con marca del plan para no duplicar llamadas
				await this.setStepNutritionIrrigationData({ ...irrigationV2, _planId: this.id });

				await this.updateEnableWatchers(false);
				await this.updateWizardData(plan);
				await this.updateEnableWatchers(true);
			}
			this.setStep(0);

			const isEmptyEditingStepData =
				this.editingStepData.initDate === '' &&
				this.editingStepData.endDate === '' &&
				this.editingStepData.timeValues.length === 0;

			if (this.isEditing && isEmptyEditingStepData) {
				this.setEditingStepData({
					initDate: this.stepData.initDate,
					endDate: this.stepData.endDate,
					timeValues: this.stepData.timeValues,
				});
			}

			if (
				!this.$f7router.previousRoute.path.startsWith(
					'/utilities/nutrition/nutrition',
				)
			) {
				this.$f7router.on('routeChange', this.resetValues);
			}

			this.loaded = true;
			this.setIsViewing(false);
		},
		beforeDestroy() {
			this.setEditingFromContextMenu(null);
		},
		methods: {
			resetValues(newRoute) {
				if (!newRoute.path.startsWith('/utilities/nutrition/nutrition')) {
					this.$f7router.off('routeChange', this.resetValues);
				}
			},
			buildIrrigationV2FromLegacyPlan(plan) {
				const products = Array.isArray(plan?.contribution_products)
					? plan.contribution_products
					: [];

				const periodsCount = products[0]?.periods?.length || 0;

				// si no hay periodos, devolvemos v2 vacío estándar
				if (!periodsCount) {
					return {
					dosis_unit: 1,
					showTotals: false,
					showSectorDetails: false,
					data: [],
					};
				}

				const period = plan?.period || 'MENSUAL';
				const start = moment(plan.init_date, 'YYYY-MM-DD');
				const irrigationTimes = Array.isArray(plan?.periods_irrigation_time)
					? plan.periods_irrigation_time
					: [];

				// Construimos year/items/month/dates como espera dosageProduct/index.vue
				const byYear = new Map();

				for (let i = 0; i < periodsCount; i++) {
					const d =
					period === 'SEMANAL'
						? start.clone().add(i, 'week')
						: period === 'DIARIO'
						? start.clone().add(i, 'day')
						: start.clone().add(i, 'month'); // MENSUAL (y default)

					const year = String(d.year());
					const month = d.clone().locale('es').format('MMMM'); // 'noviembre', etc.
					const key = d.format('DD/MM/YYYY');

					if (!byYear.has(year)) byYear.set(year, new Map());
					const byMonth = byYear.get(year);
					if (!byMonth.has(month)) byMonth.set(month, {});
					const datesObj = byMonth.get(month);

					datesObj[key] = {
					irrigation_time: irrigationTimes[i] ?? null,
					products: products.map((p) => ({
						prodId: p.product,
						name: p.product_name,
						dosis: Number(p?.periods?.[i] || 0),
						totals: null,
						sectors: null,
					})),
					};
				}

				const data = Array.from(byYear.entries()).map(([year, monthMap]) => ({
					year,
					items: Array.from(monthMap.entries()).map(([month, dates]) => ({
					month,
					dates,
					})),
				}));

				return {
					dosis_unit: 1,
					showTotals: false,
					showSectorDetails: false,
					data,
				};
			},
			...mapActions('nutritionStepper', [
				'updateClasses',
				'setStep',
				'resetWizard',
				'updateWizardData',
				'updateEnableWatchers',
				'setEditingStepData',
				'setStepNutritionIrrigationData',
			]),
			...mapActions('Nutrition', ['setEditingFromContextMenu']),
			...mapActions('nutritionStepper', ['setIsViewing']),
		},
	};
</script>
<style src="./nutrition-step-one.scss" scoped lang="scss"></style>
